/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { html, View } from 'rune-ts';
import klass from './GnbCreatorShop24View.module.scss';
import {
  CartFillIcon,
  HeartFillIcon,
  PrimaryLogoTypeIcon,
  UserFillIcon,
} from '@services/MarppleShop/renderApp/components/atoms/Icon';
import { InputSearch } from '@services/MarppleShop/renderApp/components/atoms/InputSearch/InputSearch.ts';
import {
  CreatorLogoSearch,
  CreatorSearchStatusChange,
} from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorLogoSearch.ts';
import { SearchModuleCreatorShop } from '@services/MarppleShop/renderApp/components/cells/SearchModule/SearchModuleCreatorShop.ts';
import { getSearchInputPlaceholder } from '@services/MarppleShop/features/Search/util';
import { classes } from '@services/MarppleShop/shared/util';
import { throttleAnimationFrameLevel } from '@modules/Util/F/Function/animation';
import { makeMainUrl } from '@services/MarppleShop/shared/util/url.ts';
import { PostMessageNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { CreatorLogo } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorLogo.ts';
import { CartCountView } from '@services/MarppleShop/renderApp/components/atoms/Icon/Icons.ts';

const SCROLL_Y_THRESHOLD_PX = 10;

type TTheme = 'Dark' | 'Light' | 'Logo';

const classesByTheme: Record<TTheme, string> = {
  Logo: klass.logo_theme,
  Dark: klass.dark_theme,
  Light: '',
};

export type TGnbCreatorShop24ViewProps = {
  theme: TTheme;
  cart_count: number;
  creator: {
    id: number;
    profile_url: string | null;
    name: string | null;
    domain_name: string;
    link: string;
  };
  search:
    | {
        value?: string;
        placeholder?: string;
      }
    | undefined;
  plus:
    | {
        domain_name: string;
        logo_url: string | null;
        name: string;
        link?: string;
        navigate?: PostMessageNavigate;
      }
    | undefined;
};

/**
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=22309-16015&m=dev
 *
 * @see HeaderCreatorShop
 */
export class GnbCreatorShop24View extends View<TGnbCreatorShop24ViewProps> {
  private initial_theme: TGnbCreatorShop24ViewProps['theme'];

  private creator_logo_view = new CreatorLogo({
    plus: this.data.plus,
    creator: this.data.creator,
    is_mobile: false,
    theme: 'bright',
  });

  private creator_logo_search_view_for_input_search = new CreatorLogoSearch({
    profile_url: this.data.creator.profile_url,
  });

  private creator_logo_search_view_for_search_module_creator_shop = new CreatorLogoSearch({
    profile_url: this.data.creator.profile_url,
  });

  private input_search_view = new InputSearch({
    value: this.data.search?.value,
    placeholder: this.data.search?.placeholder,
    transparent: this.data.theme === 'Dark',
    backdrop_filter: this.data.theme !== 'Dark',
    off_reset: true,
    left_part: this.creator_logo_search_view_for_input_search,
    is_creator_shop: true,
  });

  private search_module_creator_shop_view = new SearchModuleCreatorShop(this.data.search ?? {}, {
    klass: klass.search_module,
    is_mobile: false,
    highlight: 'fill',
    search_input_left_part: this.creator_logo_search_view_for_search_module_creator_shop,
    search_type: 'CREATOR_SHOP',
    store_id: this.data.creator.id,
    store_domain_name: this.data.creator.domain_name,
  });

  constructor(props: TGnbCreatorShop24ViewProps) {
    super(props);
    this.initial_theme = props.theme;
  }

  setTheme(theme: TTheme) {
    this.data.theme = theme;

    const element = this.element();

    const theme_classes = Object.entries(classesByTheme)
      .map(([, v]) => v)
      .filter(Boolean);
    theme_classes.forEach((v) => element.classList.remove(v));
    theme_classes.map((v) => element.querySelectorAll(`.${v}`).forEach((el) => el.classList.remove(v)));

    const logo_wrapper_element = element.querySelector('.logo_wrapper')!;
    if (theme === 'Logo') {
      logo_wrapper_element.innerHTML = this.creator_logo_view.toHtml();
    } else {
      logo_wrapper_element.innerHTML = html`<a
        href="${makeMainUrl(`/${ET.lang}`)}"
        class="${classes(klass.primary_logo_type_icon, classesByTheme[this.data.theme])}"
      >
        ${PrimaryLogoTypeIcon}
      </a>`.make(this);
    }

    if (classesByTheme[theme]) {
      element.classList.add(classesByTheme[theme]);
      element.querySelector(`.${klass.primary_logo_type_icon}`)?.classList.add(classesByTheme[theme]);
      element
        .querySelectorAll(`.${klass.utility_item}`)
        .forEach((el) => el.classList.add(classesByTheme[theme]));
    }

    if (theme === 'Dark') {
      this.input_search_view.element().classList.add(InputSearch.TRANSPARENT_KLASS);
    } else {
      this.input_search_view.element().classList.remove(InputSearch.TRANSPARENT_KLASS);
    }
  }

  /**
   * @see https://github.com/marpple/marpple-web/blob/65fc3cf25a5cdbef49f74c0a03660325aaa53f92/services/MarppleShop/renderApp/components/cells/Header/Base/HeaderAbstract.ts#L168-L187
   */
  setCartCount(cart_count: number) {
    this.data.cart_count = cart_count;

    const cartCountView = this.subView(CartCountView);

    if (!this.data.cart_count) {
      cartCountView?.element().remove();
      return this;
    }

    if (cartCountView) {
      cartCountView.setCount(this.data.cart_count).redraw();
    } else {
      this.element()
        .querySelector('.cart-fill-icon')
        ?.append(new CartCountView({ count: this.data.cart_count }).render());
    }
  }

  protected override onRender() {
    this.delegate('focusin', InputSearch, (event) => {
      if (event.target !== this.creator_logo_search_view_for_input_search.element()) {
        this.search_module_creator_shop_view.open();
      }
    });

    document.addEventListener('click', (event) => {
      if (!this.element().contains(event.target as HTMLElement)) {
        const value = this.search_module_creator_shop_view.close();
        this.input_search_view.setValue(value);
      }
    });

    /**
     * @see https://github.com/marpple/marpple-web/blob/b73596b5a73537b9d0dfa0fa94d3e4a7e11e8472/services/MarppleShop/renderApp/components/cells/Header/CreatorShop/HeaderCreatorShop.ts#L313-L326
     */
    this.delegate(CreatorSearchStatusChange, CreatorLogoSearch, (event) => {
      this.search_module_creator_shop_view.changeSearchType(
        event.detail.status === 'enable' ? 'CREATOR_SHOP' : 'ALL',
      );
      this.creator_logo_search_view_for_search_module_creator_shop.changeStatus(event.detail.status);
      if (this.creator_logo_search_view_for_input_search.isRendered()) {
        this.creator_logo_search_view_for_input_search.changeStatus(event.detail.status);
      }

      const next_placeholder = getSearchInputPlaceholder(ET.lang);
      this.search_module_creator_shop_view.InputSearchView.data.placeholder = next_placeholder;
      this.search_module_creator_shop_view.InputSearchView.redraw();
      this.input_search_view.data.placeholder = next_placeholder;
      if (this.initial_theme === 'Dark') {
        this.input_search_view.data.transparent = window.scrollY <= SCROLL_Y_THRESHOLD_PX;
      }
      this.input_search_view.redraw();
    });

    window.addEventListener(
      'scroll',
      throttleAnimationFrameLevel(() => {
        if (document.body.classList.contains('body-fixed') || document.body.classList.contains('fixed')) {
          return;
        }

        const next_theme = window.scrollY > SCROLL_Y_THRESHOLD_PX ? 'Logo' : this.initial_theme;
        if (next_theme !== this.data.theme) {
          this.setTheme(next_theme);
        }
      }) as any,
    );
  }

  protected override onMount() {
    this.setTheme(window.scrollY > SCROLL_Y_THRESHOLD_PX ? 'Logo' : this.initial_theme);
  }

  protected override template(props: TGnbCreatorShop24ViewProps) {
    return html`<nav class="${classes(klass.gnb_creator_shop_24, classesByTheme[props.theme])}">
      <div class="${klass.contents}">
        <div class="logo_wrapper">
          ${props.theme === 'Logo'
            ? this.creator_logo_view
            : html`<a
                href="${makeMainUrl(`/${ET.lang}`)}"
                class="${classes(klass.primary_logo_type_icon, classesByTheme[props.theme])}"
              >
                ${PrimaryLogoTypeIcon}
              </a>`}
        </div>

        <div class="${klass.utility}">
          <div class="${klass.search}">${this.input_search_view} ${this.search_module_creator_shop_view}</div>

          <div class="${klass.utility_list}">
            <a
              href="/${ET.lang}/@/my_like"
              class="${classes(klass.utility_item, classesByTheme[props.theme])}"
              >${HeartFillIcon}</a
            >
            <a href="/${ET.lang}/@/cart" class="${classes(klass.utility_item, classesByTheme[props.theme])}"
              >${CartFillIcon({ count: props.cart_count })}</a
            >
            <a href="/${ET.lang}/@/mypage" class="${classes(klass.utility_item, classesByTheme[props.theme])}"
              >${UserFillIcon}</a
            >
          </div>
        </div>
      </div>
    </nav>`;
  }
}
